import { Facebook, Instagram, Twitter, Truck, RotateCcw, ShieldCheck, Headphones } from "lucide-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import logoAsset from "@/assets/royal-lumina-logo.png";
import { API_BASE_URL } from "@/lib/api";

type FooterLink = { label: string; to: string };
const cols: { title: string; links: FooterLink[] }[] = [
  {
    title: "Quick Links",
    links: [
      { label: "Home", to: "/" },
      { label: "Shop", to: "/shop" },
      { label: "Collections", to: "/collections" },
      { label: "About Us", to: "/about" },
      { label: "Contact Us", to: "/contact" },
    ],
  },
  {
    title: "Customer Care",
    links: [
      { label: "My Account", to: "/account" },
      { label: "Track Order", to: "/account/orders" },
      { label: "Wishlist", to: "/account/wishlist" },
      { label: "Addresses", to: "/account/addresses" },
      { label: "Cart", to: "/cart" },
    ],
  },
  {
    title: "Policies",
    links: [
      { label: "Privacy Policy", to: "/contact" },
      { label: "Terms of Service", to: "/contact" },
      { label: "Refund Policy", to: "/contact" },
      { label: "Contact Support", to: "/contact" },
    ],
  },
];

const socials: { icon: typeof Facebook; href: string; label: string }[] = [
  { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
  { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
  { icon: Twitter, href: "https://twitter.com", label: "Twitter" },
];

export function Footer() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/settings`);
        if (res.ok) return await res.json();
      } catch (err) {
        // silent fallback
      }
      return null;
    },
    enabled: pathname === "/",
  });

  // Only show footer on the homepage for a clean, app-like experience on shop, orders, cart & account pages
  if (pathname !== "/") {
    return null;
  }

  const freeShipLimit = settings?.freeShippingThreshold !== undefined ? settings.freeShippingThreshold : 999;

  const trust = [
    { icon: Truck, title: "Free Shipping", desc: `Above ₹${freeShipLimit}` },
    { icon: RotateCcw, title: "Easy Returns", desc: "Within 7 days" },
    { icon: ShieldCheck, title: "Secure Payment", desc: "100% protected" },
    { icon: Headphones, title: "24/7 Support", desc: "Here to help" },
  ];
  return (
    <footer className="bg-primary text-cream mt-8">
      <div className="border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8 py-10">
          {trust.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-center gap-4">
              <Icon className="h-6 w-6 text-gold" strokeWidth={1.4} />
              <div>
                <div className="text-sm font-medium">{title}</div>
                <div className="text-xs text-cream/50">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
        <div className="col-span-2 flex flex-col items-center text-center md:items-start md:text-left">
          <Link to="/" aria-label="Royal Lumina" className="inline-flex justify-center md:justify-start">
            <img
              src={logoAsset}
              alt="Royal Lumina — Scented Candles"
              className="h-28 sm:h-36 md:h-40 lg:h-44 w-auto object-contain filter drop-shadow-md"
            />
          </Link>
          <p className="mt-4 text-sm text-cream/60 max-w-sm leading-relaxed">
            Royal Lumina brings you handcrafted scented candles that add warmth,
            beauty and tranquility to your life.
          </p>
          <div className="mt-6 flex items-center justify-center md:justify-start gap-3">
            {socials.map(({ icon: I, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="h-10 w-10 rounded-full border border-white/15 grid place-items-center hover:bg-gold hover:text-primary hover:border-gold transition-all">
                <I className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        {cols.map((c) => (
          <div key={c.title}>
            <h4 className="text-xs tracking-[0.25em] uppercase text-gold font-medium">{c.title}</h4>
            <ul className="mt-5 space-y-3 text-sm text-cream/70">
              {c.links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-gold transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-cream/40">
          <div>© 2026 Royal Lumina. All rights reserved.</div>
          <div className="tracking-[0.25em] uppercase">Visa · Mastercard · UPI · PayPal</div>
        </div>
      </div>
    </footer>
  );
}
